/**
 * selected-space-context-storage.ts
 *
 * Browser helpers for persisting selected Space context by user/workspace.
 *
 * Primary responsibilities:
 * - Persist selected Space and selected Space item per user/workspace scope
 * - Restore sanitized Space context during app bootstrap
 * - Clear scoped context on sign-out or workspace reset flows
 *
 * Dependencies:
 * - Browser localStorage
 * - Spaces shared types
 */

import type { Tables } from '@/supabase/types'
import type { SelectedSpaceItem } from '@/types/spaces'

const SELECTED_SPACE_CONTEXT_STORAGE_PREFIX = 'selectedSpaceContext'
const SPACE_ITEM_TYPES = new Set([
  'task',
  'sheet',
  'note',
  'sprint',
  'folder',
  'conversation'
])

interface SelectedSpaceContextStorageScope {
  userId?: string | null
  workspaceId?: string | null
}

interface SelectedSpaceContextStoragePayload {
  selectedSpace: Tables<'spaces'> | null
  selectedSpaceItem: SelectedSpaceItem
}

// --------------

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

// --------------

function normalizeId(value: string | null | undefined) {
  if (typeof value !== 'string') {
    return null
  }

  const normalizedValue = value.trim()
  return normalizedValue.length > 0 ? normalizedValue : null
}

// --------------

function getSpaceContextStorageKey(scope: SelectedSpaceContextStorageScope) {
  const normalizedUserId = normalizeId(scope.userId)
  const normalizedWorkspaceId = normalizeId(scope.workspaceId)

  if (!normalizedUserId || !normalizedWorkspaceId) {
    return null
  }

  return `${SELECTED_SPACE_CONTEXT_STORAGE_PREFIX}:${normalizedUserId}:${normalizedWorkspaceId}`
}

// --------------

function getSpaceContextStorageKeys(
  storage: Storage,
  scope?: SelectedSpaceContextStorageScope
) {
  const scopedStorageKey = scope ? getSpaceContextStorageKey(scope) : null

  if (scopedStorageKey) {
    return [scopedStorageKey]
  }

  const normalizedUserId = normalizeId(scope?.userId)
  const prefix = normalizedUserId
    ? `${SELECTED_SPACE_CONTEXT_STORAGE_PREFIX}:${normalizedUserId}:`
    : `${SELECTED_SPACE_CONTEXT_STORAGE_PREFIX}:`
  const keys: string[] = []

  for (let index = 0; index < storage.length; index++) {
    const key = storage.key(index)

    if (key?.startsWith(prefix)) {
      keys.push(key)
    }
  }

  return keys
}

// --------------

function normalizeSelectedSpace(
  value: unknown,
  workspaceId: string
): Tables<'spaces'> | null {
  if (!isRecord(value)) {
    return null
  }

  const spaceId = normalizeId(typeof value.id === 'string' ? value.id : null)
  const spaceWorkspaceId = normalizeId(
    typeof value.workspace_id === 'string' ? value.workspace_id : null
  )

  if (!spaceId || !spaceWorkspaceId || spaceWorkspaceId !== workspaceId) {
    return null
  }

  return value as Tables<'spaces'>
}

// --------------

function normalizeSelectedSpaceItem(
  value: unknown,
  selectedSpaceId: string | null
): SelectedSpaceItem {
  if (value === null) {
    return null
  }

  if (!isRecord(value)) {
    return null
  }

  const itemType = typeof value.type === 'string' ? value.type : null

  if (!itemType || !SPACE_ITEM_TYPES.has(itemType)) {
    return null
  }

  const itemId = normalizeId(typeof value.id === 'string' ? value.id : null)
  const itemData = isRecord(value.data) ? value.data : null
  const itemDataId = normalizeId(
    typeof itemData?.id === 'string' ? itemData.id : null
  )
  const itemDataSpaceId = normalizeId(
    typeof itemData?.space_id === 'string' ? itemData.space_id : null
  )

  if (!itemId || !itemData || !itemDataId || !itemDataSpaceId) {
    return null
  }

  if (itemId !== itemDataId) {
    return null
  }

  if (selectedSpaceId && itemDataSpaceId !== selectedSpaceId) {
    return null
  }

  return value as SelectedSpaceItem
}

// --------------

export function readSelectedSpaceContextFromStorage(
  scope: SelectedSpaceContextStorageScope
): SelectedSpaceContextStoragePayload | null {
  if (typeof window === 'undefined') {
    return null
  }

  const storageKey = getSpaceContextStorageKey(scope)
  const normalizedWorkspaceId = normalizeId(scope.workspaceId)

  if (!storageKey || !normalizedWorkspaceId) {
    return null
  }

  try {
    const rawPayload = window.localStorage.getItem(storageKey)

    if (!rawPayload) {
      return null
    }

    const parsedPayload = JSON.parse(rawPayload)

    if (!isRecord(parsedPayload)) {
      window.localStorage.removeItem(storageKey)
      return null
    }

    const selectedSpace = normalizeSelectedSpace(
      parsedPayload.selectedSpace,
      normalizedWorkspaceId
    )
    const selectedSpaceItem = normalizeSelectedSpaceItem(
      parsedPayload.selectedSpaceItem,
      selectedSpace?.id || null
    )

    if (!selectedSpace && !selectedSpaceItem) {
      window.localStorage.removeItem(storageKey)
      return null
    }

    return {
      selectedSpace,
      selectedSpaceItem
    }
  } catch {
    return null
  }
}

// --------------

export function persistSelectedSpaceContextToStorage(
  scope: SelectedSpaceContextStorageScope,
  payload: SelectedSpaceContextStoragePayload
) {
  if (typeof window === 'undefined') {
    return
  }

  const storageKey = getSpaceContextStorageKey(scope)
  const normalizedWorkspaceId = normalizeId(scope.workspaceId)

  if (!storageKey || !normalizedWorkspaceId) {
    return
  }

  try {
    const selectedSpace = normalizeSelectedSpace(
      payload.selectedSpace,
      normalizedWorkspaceId
    )
    const selectedSpaceItem = normalizeSelectedSpaceItem(
      payload.selectedSpaceItem,
      selectedSpace?.id || null
    )

    if (!selectedSpace && !selectedSpaceItem) {
      window.localStorage.removeItem(storageKey)
      return
    }

    window.localStorage.setItem(
      storageKey,
      JSON.stringify({
        selectedSpace,
        selectedSpaceItem
      })
    )
  } catch {
    // Ignore storage failures so in-memory Space state stays responsive.
  }
}

// --------------

export function clearSelectedSpaceContextFromStorage(
  scope?: SelectedSpaceContextStorageScope
) {
  if (typeof window === 'undefined') {
    return
  }

  try {
    const keysToClear = getSpaceContextStorageKeys(window.localStorage, scope)
    keysToClear.forEach((key) => window.localStorage.removeItem(key))
  } catch {
    // Ignore storage cleanup failures during sign-out/reset flows.
  }
}

// --------------

export { SELECTED_SPACE_CONTEXT_STORAGE_PREFIX }
